import React, { useState } from 'react';
import { styles } from './AlternativesProduct.css';
import { AlgoliaHitTransformer } from '../../Algolia/transformers/AlgoliaHitTransformer';
import { ProgressiveImage } from '../../hooks/useProgressiveImage/useProgressiveImage';
import { ProductImageFallback } from '../../ProductImageFallback/ProductImageFallback';
import { ProductCardActions } from '../../ProductCard/ProductCardActions';
import { CartActionState } from '../../CartProvider/types';
import PriceTag from '@jbhi-fi/lanyard-ui/components/PriceTag';

export type AlternativesProductProps = {
  title: string;
  productImage: string;
  deliveryTag?: string;
  price: {
    ticket: number;
    actual: number;
    cashback: boolean | undefined;
    perks: number | undefined;
  };
  onAddToCart: (e: React.SyntheticEvent) => Promise<boolean>;
};

export const AlternativesProduct = ({
  title,
  price,
  deliveryTag,
  productImage,
  onAddToCart,
}: AlternativesProductProps) => {
  const [cartState, setCartState] = useState<CartActionState>(CartActionState.Default);
  const hasDeliveryTag = !!deliveryTag;
  const hasCashbackTag = !!price.cashback;
  const hasPerksTag = !!price.perks;

  const handleAddToCart = async (e: React.SyntheticEvent) => {
    setCartState(CartActionState.Updating);
    const wasAdded = await onAddToCart(e);

    if (wasAdded) {
      setCartState(CartActionState.Complete);
      return true;
    } else {
      setCartState(CartActionState.Default);
      return false;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.background} />
      <div className={styles.leftContent}>
        <span>Viewing product:</span>
        <div className={styles.imageContainer}>
          <div className={styles.image}>
            {productImage ? (
              <ProgressiveImage
                className={styles.image}
                {...AlgoliaHitTransformer.toProgressiveImageProps(
                  { product_image: productImage, title },
                  { sizePx: 300 }
                )}
              />
            ) : (
              <ProductImageFallback className={styles.image} />
            )}
          </div>
          <div className={styles.title} data-testid="product-card-title">
            {title}
          </div>
        </div>
      </div>
      <div
        className={styles.rightContent({
          hasDeliveryTag: hasDeliveryTag,
          hasCashbackTag: hasCashbackTag,
          hasPerksTag: hasPerksTag,
        })}
      >
        <PriceTag
          className={styles.price({
            hasCashbackTag: hasCashbackTag,
            hasPerksTag: hasPerksTag,
          })}
          deliveryTag={deliveryTag}
          variant="extraSmall"
          tooltipPosition="top"
          {...price}
        />
        <div className={styles.actions}>
          <ProductCardActions
            variant="minified"
            testId="alternatives-product-card-actions"
            type="action"
            label="Add to cart"
            onAction={handleAddToCart}
            isLoading={cartState === CartActionState.Updating}
            isConfirmationEnabled={true}
            shouldRemainInAddedState={true}
          />
        </div>
      </div>
    </div>
  );
};
